import React from 'react';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Form, Radio } from 'antd';
import {
	Walking,
	Sitting,
	Kneeling,
	OneBentLeg,
	TwoFlexedLegs,
	OneStraightLeg,
	TwoStraightLegs
} from '@/assets/svg/ergonomic_tools/owas/lower_limbs';
import { Text, Title } from '@/components/Typography';
import { InfoTooltip } from '@/views/ListReport/Report/Owas/components/InfoTooltip';
import { OWAS_CATEGORIES, OWAS_SUB_CATEGORIES } from '@/views/ListReport/Report/Owas/enums';
import { OneArmUp, TwoArmsDown, TwoArmsUp } from '@/assets/svg/ergonomic_tools/owas/upper_limbs';
import { Bent, BentRotated, Straight, StraightRotated } from '@/assets/svg/ergonomic_tools/owas/torso';
import type { Category } from '@/core/domain/owas-report/category';
import * as S from './styles';

interface CategoryOptionsProps {
	fieldName: string[];
	data: Category;
}

const subCategoryIcons: Record<string, JSX.Element> = {
	[OWAS_SUB_CATEGORIES.STRAIGHT]: <Straight />,
	[OWAS_SUB_CATEGORIES.BENT]: <Bent />,
	[OWAS_SUB_CATEGORIES.STRAIGHT_AND_ROTATED]: <StraightRotated />,
	[OWAS_SUB_CATEGORIES.BENT_AND_ROTATED]: <BentRotated />,
	[OWAS_SUB_CATEGORIES.TWO_ARMS_DOWN]: <TwoArmsDown />,
	[OWAS_SUB_CATEGORIES.ONE_ARM_UP]: <OneArmUp />,
	[OWAS_SUB_CATEGORIES.TWO_ARMS_UP]: <TwoArmsUp />,
	[OWAS_SUB_CATEGORIES.SITTING]: <Sitting className="lower_limbs" />,
	[OWAS_SUB_CATEGORIES.STANDING_ON_TWO_STRAIGHT_LEGS]: <TwoStraightLegs className="lower_limbs" />,
	[OWAS_SUB_CATEGORIES.STANDING_ON_ONE_STRAIGHT_LEG]: <OneStraightLeg className="lower_limbs" />,
	[OWAS_SUB_CATEGORIES.STANDING_OR_SQUATTING_ON_TWO_FLEXED_LEGS]: <TwoFlexedLegs className="lower_limbs" />,
	[OWAS_SUB_CATEGORIES.STANDING_OR_SQUATTING_ON_ONE_BENT_LEG]: <OneBentLeg className="lower_limbs" />,
	[OWAS_SUB_CATEGORIES.KNEELING]: <Kneeling className="lower_limbs" />,
	[OWAS_SUB_CATEGORIES.WALKING]: <Walking className="lower_limbs" />
};

const { useWatch } = Form;

export function CategoryOptions({ fieldName, data }: Readonly<CategoryOptionsProps>): JSX.Element {
	const fieldValue = useWatch([...fieldName, data.name]);

	return (
		<S.Container gutter={[0, 30]}>
			<Col span={24}>
				<Row justify="start" align="middle">
					<Col>
						<Title level={4} style={{ margin: 0 }}>
							{data.description}
						</Title>
					</Col>
					{data.name === OWAS_CATEGORIES.TORSO && (
						<Col>
							<InfoTooltip message="Computer vision does not detect back twisting." />
						</Col>
					)}
				</Row>
			</Col>
			<Col span={24}>
				<Form.Item name={[...fieldName, data.name]} style={{ margin: 0 }}>
					<S.RadioGroup>
						<Row justify="center" gutter={[30, 30]}>
							{data.sub_categories.map(({ id, name, description, value }) => (
								<S.CardContainer key={id}>
									<Radio.Button value={id}>
										<Row justify="center">
											<S.CardScore span={24} $isSelected={fieldValue === id}>
												<Text>{value}</Text>
											</S.CardScore>
											<Col span={24}>{subCategoryIcons[name]}</Col>
											<Col span={24}>
												<Text>{I18n.get(description)}</Text>
											</Col>
										</Row>
									</Radio.Button>
								</S.CardContainer>
							))}
						</Row>
					</S.RadioGroup>
				</Form.Item>
			</Col>
		</S.Container>
	);
}
